import { Fragment, useState, useEffect } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { KeenIcon } from '@/components';
import { Editor } from '@tinymce/tinymce-react';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import {
  useGetPageQuery,
  useCreatePageMutation,
  useUpdateContentMutation,
  useDeleteContentMutation,
} from '@/services/CmsService';
import {
  languageOptions,
  Page,
  PageType,
  sectionData,
} from '@/types/cms.types';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { useDispatch } from 'react-redux';
import { setLanguage } from '@/store/authSlice';
import { ErrorApiResponse } from '@/types/api.types';
import { tinymceInit, TINYMCE_SCRIPT_SRC } from '@/constant';
import { usePermissions } from '@/hooks/usePermissions';
import NumberInput from '@/components/inputControls/NumberInput';
import { LegalEffectiveDateField } from '@/components/cms/LegalEffectiveDateField';
import { formatLegalEffectiveDate } from '@/utils/legalEffectiveDate';
import { useIntl } from 'react-intl';

// Type definitions
interface PrivacySection {
  id?: string;
  title: string;
  description: string;
  display_order: number;
}

const PrivacyPolicy = () => {
  const { currentLayout } = useLayout();
  const [sections, setSections] = useState<PrivacySection[]>([]);
  const [activeAccordionIndex, setActiveAccordionIndex] = useState<
    number | null
  >(null);
  const [currentSection, setCurrentSection] = useState<PrivacySection>({
    title: '',
    description: '',
    display_order: 1,
  });
  const [errors, setErrors] = useState<{
    title?: string;
    description?: string;
    display_order?: string;
  }>({});
  const [isEditing, setIsEditing] = useState(false);
  const [pageId, setPageId] = useState<string | null>(null);
  const [legalEffectiveFrom, setLegalEffectiveFrom] = useState<string | null>(null);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const language = useSelector((state: RootState) => state.auth.language);
  const { hasPermission } = usePermissions();
  // API hooks
  const {
    data: pageData,
    isLoading,
    error,
  } = useGetPageQuery({ pageType: PageType.PRIVACY, language });
  const [createPage] = useCreatePageMutation();
  const [updateContent] = useUpdateContentMutation();
  const [deleteContent] = useDeleteContentMutation();
  const dispatch = useDispatch();
  // Fetch sections on mount
  useEffect(() => {
    if (pageData?.data) {
      const privacyPage = pageData.data.find(
        (page: Page) => page.pageType === PageType.PRIVACY
      );
      if (privacyPage) {
        setPageId(privacyPage.id);
        setLegalEffectiveFrom(privacyPage.legal_effective_from ?? null);
        setSections(
          privacyPage.content
            .map((item: sectionData) => ({
              id: item.id,
              title: item.title,
              description: item.content,
              display_order: item.display_order || 1,
            }))
            .sort(
              (a: PrivacySection, b: PrivacySection) =>
                a.display_order - b.display_order
            )
        );
      }
    }
    if (error) {
      enqueueSnackbar('Failed to fetch Privacy Policy', { variant: 'error' });
    }
  }, [pageData, error, enqueueSnackbar]);

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setLanguage(e.target.value)); // Update language in Redux
  };

  // Validate section
  const validateSection = (): boolean => {
    const newErrors: {
      title?: string;
      description?: string;
      display_order?: string;
    } = {};
    let isValid = true;

    if (!currentSection.title.trim()) {
      newErrors.title = 'Title is required';
      enqueueSnackbar('Title is required', { variant: 'error' });
      isValid = false;
    }
    if (
      !currentSection.description.trim() ||
      currentSection.description === '<p><br></p>'
    ) {
      newErrors.description = 'Description is required';
      enqueueSnackbar('Description is required', { variant: 'error' });
      isValid = false;
    }
    if (currentSection.display_order < 1) {
      newErrors.display_order = 'Display order must be at least 1';
      enqueueSnackbar('Display order must be at least 1', { variant: 'error' });
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Validate all sections
  const areSectionsValid = (): boolean => {
    return sections.every(
      (section) =>
        section.title.trim() &&
        section.description.trim() &&
        section.description !== '<p><br></p>' &&
        section.display_order >= 1
    );
  };

  // Handle accordion toggle
  const handleToggleAccordion = (index?: number) => {
    if (activeAccordionIndex !== null && index === undefined) {
      enqueueSnackbar(
        'Please save or cancel the current section before adding a new one',
        {
          variant: 'warning',
        }
      );
      return;
    }

    if (index !== undefined) {
      setCurrentSection(sections[index]);
      setIsEditing(true);
      setActiveAccordionIndex(index);
    } else {
      setCurrentSection({ title: '', description: '', display_order: 1 });
      setIsEditing(false);
      setActiveAccordionIndex(sections.length);
    }
    setErrors({});
  };

  // Handle save section
  const handleSaveSection = async () => {
    if (!validateSection()) return;

    if (
      isEditing &&
      activeAccordionIndex !== null &&
      sections[activeAccordionIndex]?.id
    ) {
      // Update existing section
      try {
        await updateContent({
          pageId,
          contentId: sections[activeAccordionIndex].id,
          body: {
            title: currentSection.title,
            content: currentSection.description,
            display_order: currentSection.display_order,
          },
        }).unwrap();
        setSections(
          sections
            .map((s, i) =>
              i === activeAccordionIndex ? { ...currentSection, id: s.id } : s
            )
            .sort((a, b) => a.display_order - b.display_order)
        );
        enqueueSnackbar('Section updated successfully', { variant: 'success' });
      } catch {
        enqueueSnackbar('Failed to update section', { variant: 'error' });
      }
    } else {
      // Add new section (will be saved on submit)
      setSections(
        [...sections, currentSection].sort(
          (a, b) => a.display_order - b.display_order
        )
      );
    }
    setActiveAccordionIndex(null);
    setCurrentSection({ title: '', description: '', display_order: 1 });
    setErrors({});
    setIsEditing(false);
  };

  // Handle cancel
  const handleCancel = () => {
    setActiveAccordionIndex(null);
    setCurrentSection({ title: '', description: '', display_order: 1 });
    setErrors({});
    setIsEditing(false);
    enqueueSnackbar('Action cancelled');
  };

  // Handle delete section
  const handleDeleteSection = async (index: number) => {
    if (sections[index].id && pageId) {
      try {
        await deleteContent({ pageId, contentId: sections[index].id }).unwrap();
        enqueueSnackbar('Section deleted successfully', { variant: 'success' });
      } catch {
        enqueueSnackbar('Failed to delete section', { variant: 'error' });
      }
    }
    setSections(sections.filter((_, i) => i !== index));
    if (activeAccordionIndex === index) {
      setActiveAccordionIndex(null);
    }
  };

  // Handle final submission
  const handleSubmit = async () => {
    if (!areSectionsValid()) {
      enqueueSnackbar(
        'Please ensure all sections have valid title, description, and display order',
        { variant: 'error' }
      );
      return;
    }

    try {
      const newSections = sections.filter((section) => !section.id); // Sections without id
      if (newSections.length > 0) {
        await createPage({
          pageType: PageType.PRIVACY,
          pageId: pageId,
          content: newSections.map((section) => ({
            title: section.title,
            content: section.description,
            display_order: section.display_order,
          })),
        }).unwrap();
      }
      enqueueSnackbar('Privacy Policy submitted successfully', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar((error as ErrorApiResponse).data.message, {
        variant: 'error',
      });
    }
  };

  // Check if new section can be added
  const canAddNewSection = () => {
    return sections.every(
      (section) =>
        section.title.trim() &&
        section.description.trim() &&
        section.description !== '<p><br></p>' &&
        section.display_order >= 1
    );
  };

  // Handle blur for title input
  const handleTitleBlur = () => {
    if (activeAccordionIndex !== null && !currentSection.title.trim()) {
      setErrors((prev) => ({ ...prev, title: 'Title is required' }));
      enqueueSnackbar('Title is required', { variant: 'error' });
    } else {
      setErrors((prev) => ({ ...prev, title: undefined }));
    }
  };

  // Handle blur for description (TinyMCE)
  const handleDescriptionBlur = () => {
    if (
      activeAccordionIndex !== null &&
      (!currentSection.description.trim() ||
        currentSection.description === '<p><br></p>')
    ) {
      setErrors((prev) => ({
        ...prev,
        description: 'Description is required',
      }));
      enqueueSnackbar('Description is required', { variant: 'error' });
    } else {
      setErrors((prev) => ({ ...prev, description: undefined }));
    }
  };

  // Handle blur for display order
  const handleDisplayOrderBlur = () => {
    if (activeAccordionIndex !== null && currentSection.display_order < 1) {
      setErrors((prev) => ({
        ...prev,
        display_order: 'Display order must be at least 1',
      }));
      enqueueSnackbar('Display order must be at least 1', { variant: 'error' });
    } else {
      setErrors((prev) => ({ ...prev, display_order: undefined }));
    }
  };

  if (isLoading) {
    return (
      <div className="card card-grid min-w-full">
        <div className="card-body">
          <div className="text-center py-4" role="status" aria-live="polite">
            Loading Privacy Policy...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card card-grid min-w-full">
        <div className="card-body">
          <div
            className="text-center py-4 text-red-500"
            role="alert"
            aria-live="polite"
          >
            Failed to load Privacy Policy. Please try again.
          </div>
        </div>
      </div>
    );
  }

  if (!hasPermission('cms_management_read')) {
    return (
      <Fragment>
        {currentLayout?.name === 'demo1-layout' && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle id="privacy-policy-heading"></ToolbarPageTitle>
                <ToolbarDescription>
                  Manage Privacy Policy sections with real-time updates
                </ToolbarDescription>
              </ToolbarHeading>
            </Toolbar>
          </Container>
        )}
        <div className="container-fixed">
          <div className="card card-grid min-w-full">
            <div className="card-body">
              <div className="text-center py-4 text-gray-500">
                You don't have permission to view Privacy Policy content.
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="privacy-policy-heading"></ToolbarPageTitle>
              <ToolbarDescription>
                Manage Privacy Policy sections with real-time updates
                {legalEffectiveFrom && (
                  <span className="ml-2 inline-flex rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                    {intl.formatMessage(
                      { id: 'CMS.LEGAL_EFFECTIVE_DATE_CURRENT' },
                      { date: formatLegalEffectiveDate(legalEffectiveFrom) }
                    )}
                  </span>
                )}
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <div className="relative">
                <select
                  id="language-select"
                  value={language}
                  onChange={handleLanguageChange}
                  className="select select-bordered pl-8 pr-8 h-10 leading-normal"
                  aria-label="Select language"
                >
                  {languageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                {/* Globe icon positioned inside the select */}
                <img
                  src="/media/flags/globe.svg"
                  alt="Globe"
                  className="absolute dark:brightness-0 dark:invert contrast:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
                  aria-hidden="true"
                />
              </div>
              <div className="flex gap-2">
                {hasPermission('cms_management_create') && (
                  <button
                    className={clsx(
                      'btn btn-sm btn-light',
                      !canAddNewSection() &&
                      'btn-disabled opacity-50 cursor-not-allowed'
                    )}
                    onClick={() => handleToggleAccordion()}
                    disabled={!canAddNewSection()}
                    aria-label="Add New Section"
                  >
                    Add New Section
                  </button>
                )}
                {sections.length > 0 &&
                  hasPermission('cms_management_update') && (
                    <button
                      className={clsx(
                        'btn btn-sm btn-success flex items-center gap-2 h-[2.5rem]',
                        (!areSectionsValid() ||
                          activeAccordionIndex !== null) &&
                        'btn-disabled opacity-50 cursor-not-allowed h-[2.5rem]'
                      )}
                      onClick={handleSubmit}
                      disabled={
                        !areSectionsValid() || activeAccordionIndex !== null
                      }
                      aria-label="Submit Privacy Policy"
                    >
                      <KeenIcon
                        type="solid"
                        icon="check-circle"
                        className="text-white"
                      />
                      Submit
                    </button>
                  )}
              </div>
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed">
        <div className="card card-grid min-w-full">
          <div className="card-body">
            <LegalEffectiveDateField
              pageId={pageId}
              legalEffectiveFrom={legalEffectiveFrom}
              canUpdate={hasPermission('cms_management_update')}
            />
            {sections.length === 0 && activeAccordionIndex === null ? (
              <div className="text-center py-4 text-gray-500">
                No sections added yet. Click "Add New Section" to start.
              </div>
            ) : (
              <div className="space-y-4 px-4">
                {sections.map((section, index) => (
                  <Fragment key={section.id || index}>
                    <div className="border border-gray-200 rounded-lg">
                      <div className="flex justify-between items-center p-4">
                        <h2 className="text-lg font-semibold text-gray-800">
                          {section.title}
                        </h2>
                        <div className="flex gap-2">
                          {hasPermission('cms_management_update') && (
                            <button
                              className="btn btn-sm btn-light flex items-center gap-2"
                              onClick={() => handleToggleAccordion(index)}
                              aria-label={`Edit section ${section.title}`}
                            >
                              <KeenIcon
                                type="solid"
                                icon="pencil"
                                className="text-gray-600"
                              />
                              Edit
                            </button>
                          )}
                          {hasPermission('cms_management_delete') && (
                            <button
                              className="btn btn-sm btn-light flex items-center gap-2"
                              onClick={() => handleDeleteSection(index)}
                              aria-label={`Delete section ${section.title}`}
                            >
                              <KeenIcon
                                type="solid"
                                icon="trash"
                                className="text-gray-600"
                              />
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                      {activeAccordionIndex === index && (
                        <div className="p-4 bg-gray-200 border-t border-gray-200">
                          {/* Title Input */}
                          <div className="mb-4">
                            <label
                              htmlFor={`section-title-${index}`}
                              className="form-label"
                            >
                              Section Title
                            </label>
                            <input
                              id={`section-title-${index}`}
                              type="text"
                              className={clsx(
                                'input input-sm h-10 w-full',
                                errors.title && 'input-error'
                              )}
                              value={currentSection.title}
                              onChange={(e) =>
                                setCurrentSection({
                                  ...currentSection,
                                  title: e.target.value,
                                })
                              }
                              onBlur={handleTitleBlur}
                              placeholder="Enter section title"
                              autoComplete="off"
                            />
                            {errors.title && (
                              <span className="text-red-500 text-sm mt-1">
                                {errors.title}
                              </span>
                            )}
                          </div>
                          {/* Display Order Input */}
                          <div className="mb-4">
                            <label
                              htmlFor={`section-display-order-${index}`}
                              className="form-label"
                            >
                              Display Order
                            </label>
                            <NumberInput
                              id={`section-display-order-${index}`}
                              className={clsx(
                                'input input-sm h-10 w-full',
                                errors.display_order && 'input-error'
                              )}
                              value={currentSection.display_order}
                              onChange={(e) =>
                                setCurrentSection({
                                  ...currentSection,
                                  display_order: parseInt(e.target.value) || 0,
                                })
                              }
                              onBlur={handleDisplayOrderBlur}
                              placeholder="Enter display order"
                              autoComplete="off"
                              min="1"
                            />
                            {errors.display_order && (
                              <span className="text-red-500 text-sm mt-1">
                                {errors.display_order}
                              </span>
                            )}
                          </div>
                          {/* Description Input */}
                          <div className="mb-4">
                            <span className="form-label">Description</span>
                            <Editor
                              tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                              value={currentSection.description}
                              init={tinymceInit}
                              onEditorChange={(content: string) =>
                                setCurrentSection({
                                  ...currentSection,
                                  description: content,
                                })
                              }
                              onBlur={handleDescriptionBlur}
                              textareaName="description"
                            />
                            {errors.description && (
                              <span className="text-red-500 text-sm mt-1">
                                {errors.description}
                              </span>
                            )}
                          </div>
                          {/* Accordion Actions */}
                          <div className="flex justify-end gap-2">
                            <button
                              className="btn btn-sm btn-light"
                              onClick={handleCancel}
                              aria-label="Cancel"
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={handleSaveSection}
                              aria-label={
                                isEditing ? 'Save Changes' : 'Add Section'
                              }
                            >
                              {isEditing ? 'Save Changes' : 'Add Section'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </Fragment>
                ))}
                {/* New Section Accordion */}
                {activeAccordionIndex !== null &&
                  activeAccordionIndex === sections.length && (
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-200">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        {isEditing
                          ? 'Edit Section'
                          : `Add Section ${sections.length + 1}`}
                      </h3>
                      <div className="mb-4">
                        <label
                          htmlFor="new-section-title"
                          className="form-label"
                        >
                          Section Title
                        </label>
                        <input
                          id="new-section-title"
                          type="text"
                          className={clsx(
                            'input input-sm h-10 w-full',
                            errors.title && 'input-error'
                          )}
                          value={currentSection.title}
                          onChange={(e) =>
                            setCurrentSection({
                              ...currentSection,
                              title: e.target.value,
                            })
                          }
                          onBlur={handleTitleBlur}
                          placeholder="Enter section title"
                          autoComplete="off"
                        />
                        {errors.title && (
                          <span className="text-red-500 text-sm mt-1">
                            {errors.title}
                          </span>
                        )}
                      </div>
                      <div className="mb-4">
                        <label
                          htmlFor="new-section-display-order"
                          className="form-label"
                        >
                          Display Order
                        </label>
                        <NumberInput
                          id="new-section-display-order"
                          className={clsx(
                            'input input-sm h-10 w-full',
                            errors.display_order && 'input-error'
                          )}
                          value={currentSection.display_order}
                          onChange={(e) =>
                            setCurrentSection({
                              ...currentSection,
                              display_order: parseInt(e.target.value) || 0,
                            })
                          }
                          onBlur={handleDisplayOrderBlur}
                          placeholder="Enter display order"
                          autoComplete="off"
                          min="1"
                        />
                        {errors.display_order && (
                          <span className="text-red-500 text-sm mt-1">
                            {errors.display_order}
                          </span>
                        )}
                      </div>
                      <div className="mb-4">
                        <span className="form-label">Description</span>
                        <Editor
                          tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                          value={currentSection.description}
                          init={tinymceInit}
                          onEditorChange={(content: string) =>
                            setCurrentSection({
                              ...currentSection,
                              description: content,
                            })
                          }
                          onBlur={handleDescriptionBlur}
                          textareaName="description"
                          aria-label="Description"
                        />
                        {errors.description && (
                          <span className="text-red-500 text-sm mt-1">
                            {errors.description}
                          </span>
                        )}
                      </div>
                      {/* Accordion Actions */}
                      <div className="flex justify-end gap-2">
                        <button
                          className="btn btn-sm btn-light"
                          onClick={handleCancel}
                          aria-label="Cancel"
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleSaveSection}
                          aria-label={isEditing ? 'Save Changes' : 'Save'}
                        >
                          {isEditing ? 'Save Changes' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export { PrivacyPolicy };
